import * as React from "react";
import { EDPageLoader } from "@trailblazer-game/ed-webkit";
import dynamic from "next/dynamic";
import PageLayout from "../components/Shared/PageLayout";
import { CMSPage } from "@jitl/notion-api/src/lib/content-management-system";
import { CMSPages, PAGES_ASSET_PATH, PagesFrontmatter } from "../lib/pages-notion";
import { notionClient } from "../lib/client-notions";
import { NotionUtils } from "../helpers/notion/notion-utils";
import { GetStaticPaths } from "next/types";
import { NotionBlock, NotionSlug } from "../helpers/notion/notion";
import { CMSPageOf } from "@jitl/notion-api";

const NotionPage = dynamic(() => import("../components/Notion/NotionPage"), {
  suspense: false,
  ssr: false,
  loading: () => <EDPageLoader in={true}/>,
})

const CMS = CMSPages(false);
type CMSPagesPage = CMSPageOf<typeof CMS>;

export async function getStaticProps(context: { params: { slug: string } }) {
  const page = await CMS.loadPageBySlug(context.params.slug);

  if (!page) {
    return { notFound: true };
  }

  if (page.frontmatter.hide_in_production && process.env.NEXT_PUBLIC_ENV === 'production') {
    return { notFound: true };
  }

  const blocks = await NotionUtils.queryBlocks(notionClient.blocks.children.list as any, { block_id: page.content.id }, PAGES_ASSET_PATH);

  return {
    props: {
      page: page,
      blocks,
      dynamicMetaTags: {
        title: page.frontmatter.meta_title,
        description: page.frontmatter.meta_description,
        image: page.frontmatter.meta_image,
      }
    }
  }
}

type PageParams = { slug: string }
export const getStaticPaths: GetStaticPaths<PageParams> = async () => {
  const params: Array<{ params: PageParams }> = [];
  const slugs: NotionSlug[] = [];
  const asyncOperations = [];

  console.time(`Querying pages...`);
  const pages = await NotionUtils.query<CMSPagesPage>(CMS, {
    sorts: [
      CMS.sort.MenuOrder.ascending
    ]
  }, true);
  console.timeEnd(`Querying pages...`);

  console.time(`Creating slugs...`);
  for (const page of pages) {
    const { hide_in_production } = page.frontmatter;

    if (hide_in_production && process.env.NEXT_PUBLIC_ENV === 'production') {
      continue;
    }

    const isExternalLink = page.frontmatter.slug.startsWith('http');
    if (!isExternalLink) {
      // asyncOperations.push(async () => await NotionUtils.downloadAssets(CMS, page));
      params.push({
        params: {
          slug: page.frontmatter.slug
        }
      });
    }

    slugs.push({
      title: page.frontmatter.page_title,
      slug: isExternalLink ? page.frontmatter.slug : `/${page.frontmatter.slug}`,
      hide_in_production: page.frontmatter.hide_in_production,
      menu_group: page.frontmatter.menu_group || "Other",
      menu_order: page.frontmatter.menu_order,
      show_in_footer: page.frontmatter.show_in_footer,
      external: isExternalLink
    });
  }

  // Update slugs for the react app
  asyncOperations.push(async () => await NotionUtils.writeSlugs(slugs));
  await Promise.all(asyncOperations.map((op) => op()));
  console.timeEnd(`Creating slugs...`);

  return { paths: params, fallback: false };
}

export default function NotionWebsitePage({
                                            page,
                                            blocks
                                          }: { page: CMSPage<PagesFrontmatter> | null, blocks: NotionBlock[] }) {
  return <PageLayout
    justifyContent={"flex-start"}
    backgroundImage={page?.frontmatter.cover}>
    <NotionPage page={page} blocks={blocks}/>
  </PageLayout>
}

NotionWebsitePage.metaData = {
  title: 'News | Eternal Dragons'
}